import { Children, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './tool-motion.css';

export function usePopoverFocusDismiss(open, container, onDismiss, ...companions) {
  const latest = useRef(null), check = useRef(0);
  latest.current = { open, onDismiss };
  useEffect(() => () => { check.current += 1; }, []);
  const contains = target => container.current?.contains(target) || companions.some(ref => ref?.current?.contains(target));
  return event => {
    if (!latest.current.open || contains(event.relatedTarget)) return;
    const pending = ++check.current;
    // Replacing a focused calendar day can briefly move focus outside the
    // picker. Wait for layout refocus before deciding whether it really left.
    Promise.resolve().then(() => {
      if (pending !== check.current || !latest.current.open || !container.current?.isConnected) return;
      const focused = document.activeElement;
      if (!focused || focused === document.body || focused === document.documentElement || contains(focused)) return;
      latest.current.onDismiss();
    });
  };
}

export function usePopoverPosition(open, anchor, width = 280) {
  const [position, setPosition] = useState({});
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const place = () => {
      const rect = anchor.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight, vw = viewport?.width || window.innerWidth;
      const bottomSpace = height - rect.bottom - 16, above = bottomSpace < 220 && rect.top > bottomSpace;
      const size = Math.min(Math.max(rect.width, width), vw - 24);
      setPosition({ width: size, left: Math.max(12, Math.min(rect.left, vw - size - 12)),
        ...(above ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
        maxHeight: Math.max(120, Math.min(360, above ? rect.top - 20 : bottomSpace)) });
    };
    place(); window.addEventListener('resize', place); window.visualViewport?.addEventListener('resize', place);
    return () => { window.removeEventListener('resize', place); window.visualViewport?.removeEventListener('resize', place); };
  }, [open, anchor, width]);
  return position;
}

export default function WorkspaceSelect({ value, onChange, options, label, placeholder = 'Choose…', disabled = false, style, className = '', externalPopoverRef, ...props }) {
  const id = useId(), anchor = useRef(null), container = useRef(null), list = useRef(null), search = useRef({ text: '', time: 0 });
  const items = options.map(item => Array.isArray(item) ? { value: String(item[0]), label: item[1] } : typeof item === 'object' ? { ...item, value: String(item.value) } : { value: String(item), label: String(item) });
  const selected = items.findIndex(item => item.value === String(value ?? ''));
  const [open, setOpen] = useState(false), [active, setActive] = useState(Math.max(0, selected));
  const dismissFocus = usePopoverFocusDismiss(open, container, () => setOpen(false), list);
  const registerList = useCallback(node => { list.current = node; if (externalPopoverRef) externalPopoverRef.current = node; }, [externalPopoverRef]);
  const position = usePopoverPosition(open, anchor);
  const name = props['aria-label'] || label;
  const [inferredLabel,setInferredLabel]=useState('');
  useLayoutEffect(()=>{
    const parent=anchor.current?.closest('label');
    if(parent){const copy=parent.cloneNode(true);copy.querySelectorAll('.tl-select').forEach(element=>element.remove());setInferredLabel(copy.textContent.trim());}
  },[]);
  const choose = index => { if (!items[index] || items[index].disabled) return; onChange(items[index].value); setOpen(false); anchor.current?.focus({ preventScroll: true }); };
  const move = direction => {
    let next = active;
    for (let i = 0; i < items.length; i++) { next = (next + direction + items.length) % items.length; if (!items[next].disabled) break; }
    setActive(next);
  };
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!container.current?.contains(event.target) && !list.current?.contains(event.target)) setOpen(false); };
    const scroll = event => { if (event.target !== list.current && !list.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside); window.addEventListener('scroll', scroll, true);
    return () => { document.removeEventListener('pointerdown', outside); window.removeEventListener('scroll', scroll, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const menu = list.current, option = menu?.querySelector(`[data-index="${active}"]`);
    if (!option || !menu.clientHeight) return;
    // Reveal options inside this menu only. scrollIntoView also moves enclosing
    // dialogs/pages, whose scroll listeners intentionally dismiss popovers.
    const top = option.offsetTop, bottom = top + option.offsetHeight;
    if (top < menu.scrollTop) menu.scrollTop = top;
    else if (bottom > menu.scrollTop + menu.clientHeight) menu.scrollTop = bottom - menu.clientHeight;
  }, [open, active, position]);
  const key = event => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); return; }
    if (event.key === 'Tab') { setOpen(false); return; }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      if (!open) { setOpen(true); setActive(Math.max(0, selected)); }
      else if (event.key === 'Home') setActive(0); else if (event.key === 'End') setActive(items.length - 1); else move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (['Enter', ' '].includes(event.key)) {
      event.preventDefault(); event.stopPropagation(); if (open) choose(active); else { setActive(Math.max(0, selected)); setOpen(true); }
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault(); event.stopPropagation();
      const now = Date.now(); search.current.text = now - search.current.time > 650 ? event.key : search.current.text + event.key; search.current.time = now;
      const match = items.findIndex(item => !item.disabled && String(item.label).toLowerCase().startsWith(search.current.text.toLowerCase()));
      if (match >= 0) { setOpen(true); setActive(match); }
    }
  };
  const menu = open && <div id={`${id}-list`} ref={registerList} role="listbox" aria-label={name || inferredLabel || 'Options'} className="tl-control-popover tl-select-list" style={position} data-swipe-ignore data-tl-popover>
    {items.map((item, index) => <button type="button" role="option" id={`${id}-${index}`} key={item.value} tabIndex={-1} aria-selected={index === selected} aria-disabled={item.disabled || undefined} disabled={item.disabled} data-index={index} style={{'--option-index':index}} className={index === active ? 'is-highlighted' : ''} onPointerMove={() => setActive(index)} onMouseDown={event => event.preventDefault()} onClick={() => choose(index)}><span>{item.label || 'None'}</span>{index === selected && <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 4 4 10-10"/></svg>}</button>)}
  </div>;
  return <div ref={container} className={`tl-select ${className}`} style={style?.width === 'auto' ? { width: 'auto' } : undefined} onBlur={dismissFocus}>
    <button {...props} id={props.id || id} ref={anchor} type="button" role="combobox" aria-label={name||inferredLabel||'Choose option'} aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? `${id}-list` : undefined} aria-activedescendant={open ? `${id}-${active}` : undefined} disabled={disabled || !items.length} onKeyDown={key} onClick={() => { setActive(Math.max(0, selected)); setOpen(v => !v); }} className="tl-select-trigger" style={style}>
      <span className={!items[selected]?.label ? 'is-placeholder' : ''}>{items[selected]?.label || placeholder}</span><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m7 10 5 5 5-5"/></svg>
    </button>
    {menu && (externalPopoverRef ? createPortal(menu, document.body) : menu)}
  </div>;
}

export function ThemedSelect({children,onChange,...props}) {
  const text = nodes => Children.toArray(nodes).map(node=>typeof node==='object'?text(node.props?.children):String(node)).join('');
  const options = nodes => Children.toArray(nodes).flatMap(node=>{
    if(typeof node!=='object')return [];
    if(node.type==='optgroup')return options(node.props.children);
    const label=text(node.props.children);return [{value:node.props.value??label,label,disabled:node.props.disabled}];
  });
  return <WorkspaceSelect {...props} options={options(children)} onChange={value=>onChange?.({target:{value,name:props.name}})}/>;
}
